import React, { useEffect } from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Box,
  Image,
  Badge,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { useOnboardingStore } from '../../../stores/onboardingStore';
import { LearningAvatar } from '../../../types/onboarding';

const AvatarStep: React.FC = () => {
  const { userData, updateUserData } = useOnboardingStore();
  
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = 'transparent';
  const selectedBg = useColorModeValue('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.05)');
  const selectedBorder = useColorModeValue('blue.500', 'blue.300');
  const subtitleColor = useColorModeValue('blue.600', 'blue.300');

  const avatars = [
    {
      id: 'scholar' as LearningAvatar,
      title: 'The Scholar',
      description: 'Deep understanding through systematic study. Loves to read, take detailed notes, and master concepts thoroughly.',
      seed: 'Ryan',
      backgroundColor: '4F46E5',
      traits: ['Methodical', 'Thorough', 'Analytical'],
    },
    {
      id: 'explorer' as LearningAvatar,
      title: 'The Explorer',
      description: 'Learn by discovering and experimenting. Curious about everything and loves hands-on learning experiences.',
      seed: 'Mackenzie',
      backgroundColor: '10B981',
      traits: ['Curious', 'Adventurous', 'Hands-on'],
    },
    {
      id: 'challenger' as LearningAvatar,
      title: 'The Challenger',
      description: 'Thrive on difficult problems and competition. Motivated by challenges and enjoys to push boundaries.',
      seed: 'Garfield',
      backgroundColor: 'F97316',
      traits: ['Competitive', 'Determined', 'Focused'],
    },
    {
      id: 'creator' as LearningAvatar,
      title: 'The Creator',
      description: 'Build and create to understand concepts. Learns best by making things and expressing ideas creatively.',
      seed: 'Sophie',
      backgroundColor: 'EC4899',
      traits: ['Creative', 'Innovative', 'Visual'],
    },
  ];

  // Handle number key selection
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Handle number keys 1-4 to select avatars
      const num = parseInt(e.key);
      if (num >= 1 && num <= 4) {
        e.preventDefault();
        updateUserData('learningAvatar', avatars[num - 1].id);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [updateUserData]);

  return (
    <VStack spacing={8} py={8}>
        {/* Header */}
        <VStack spacing={4} textAlign="center" mb={8}>
          <Heading size="2xl">Choose your learning avatar</Heading>
          <Text color={textColor} fontSize="lg" maxW="700px" mx="auto">
            Select the character that best matches your learning style.
          </Text>
        </VStack>

        {/* Avatar Cards - Pokemon Style Grid */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} maxW="1000px" mx="auto" px={4}>
          {avatars.map((avatar, index) => (
            <Box
              key={avatar.id}
              bg={`linear-gradient(135deg, #${avatar.backgroundColor}22 0%, #${avatar.backgroundColor}44 100%)`}
              borderRadius="lg"
              borderWidth="3px"
              borderColor={userData.learningAvatar === avatar.id ? selectedBorder : 'transparent'}
              cursor="pointer"
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-8px)', shadow: 'xl' }}
              onClick={() => updateUserData('learningAvatar', avatar.id)}
              overflow="hidden"
              position="relative"
              minH="380px"
              maxW="220px"
              mx="auto"
              w="full"
              animation={`stackIn 0.4s ease-out ${index * 0.05}s both`}
              sx={{
                cursor: 'pointer !important',
                '& *': {
                  cursor: 'pointer !important',
                },
                '&:hover': {
                  cursor: 'pointer !important',
                },
                '@keyframes stackIn': {
                  from: {
                    opacity: 0,
                    transform: 'translateX(-30px) scale(0.95)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateX(0) scale(1)',
                  },
                },
              }}
            >
              

              {/* Selection Indicator */}
              {userData.learningAvatar === avatar.id && (
                <Box
                  position="absolute"
                  top={2}
                  right={2}
                  bg={selectedBorder}
                  color="white"
                  borderRadius="full"
                  w={8}
                  h={8}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  zIndex={2}
                >
                  <Text fontSize="lg">✓</Text>
                </Box>
              )}
              
              {/* Avatar Image Section */}
              <Box
                pt={4}
                pb={3}
                px={3}
                h="140px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
              >
                <Image
                  src={`https://api.dicebear.com/9.x/notionists/svg?seed=${avatar.seed}&backgroundColor=${avatar.backgroundColor}`}
                  alt={avatar.title}
                  boxSize="100px"
                  borderRadius="full"
                  bg="white"
                  p={2}
                  shadow="md"
                  cursor="pointer"
                />
              </Box>
              
              {/* Content Section */}
              <VStack align="center" spacing={1.5} p={3} pb={8} flex={1} justify="space-between">
                <Heading size="md" textAlign="center">{avatar.title}</Heading>
                
                <Text fontSize="xs" color={textColor} textAlign="center" px={2}>
                  {avatar.description}
                </Text>
                
                {/* Traits as small badges */}
                <HStack spacing={1} flexWrap="wrap" justify="center" mt={6}>
                  {avatar.traits.map((trait) => (
                    <Badge
                      key={trait}
                      colorScheme="gray"
                      fontSize="xs"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                    >
                      {trait}
                    </Badge>
                  ))}
                </HStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
        
        {/* Note at the bottom */}
        <Text 
          fontSize="sm" 
          color={useColorModeValue('gray.500', 'gray.500')} 
          textAlign="center" 
          maxW="600px" 
          mx="auto"
          mt={4}
          fontStyle="italic"
        >
          Don't worry, don't overthink it! This is just a starting point to understand your learning style. 
          Your AI tutor will become much more personalized as you continue to use LearnYourself.
        </Text>
    </VStack>
  );
};

export default AvatarStep;